import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { webtoons, topRated } from "@/mocks/webtoons";
import { categories } from "@/mocks/categories";

const badgeStyles = [
  "bg-accent-500 text-background-50",
  "bg-accent-300 text-foreground-900",
  "bg-accent-200 text-foreground-800",
];

export default function Ranking() {
  useEffect(() => { document.title = "免费韩漫排行榜人气推荐 · 漫糖韩漫"; }, []);

  const [filter, setFilter] = useState<string>("all");
  const [sort, setSort] = useState<"rating" | "views" | "likes">("rating");

  let list = [...webtoons];
  if (filter !== "all") list = list.filter((w) => w.category === filter);
  list = list.sort((a, b) =>
    sort === "rating" ? b.rating - a.rating : sort === "views" ? parseViews(b) - parseViews(a) : b.likes - a.likes
  );

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* breadcrumb + header */}
      <section className="bg-foreground-950">
        <div className="mx-auto max-w-7xl px-4 pb-10 pt-24 md:px-6 md:pt-28">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-background-400">
            <Link to="/" className="hover:text-background-100">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-200">人气排行榜</span>
          </nav>
          <div className="mt-4 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="flex items-center gap-1.5 text-sm font-medium text-accent-400">
                <i className="ri-trophy-fill"></i>
                免费韩漫排行榜
              </span>
              <h1 className="mt-2 font-heading text-3xl text-background-50 md:text-5xl">
                人气高分实时榜单
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300">
                按题材整理的高分榜单,每日实时更新人气推荐。从甜宠到悬疑,编辑精选口碑佳作,
                帮你快速找到对味的作品。
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setSort("rating")}
                className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                  sort === "rating" ? "bg-accent-500 text-background-50" : "bg-foreground-800 text-background-300 hover:bg-foreground-700"
                }`}
              >
                按评分
              </button>
              <button
                onClick={() => setSort("views")}
                className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                  sort === "views" ? "bg-accent-500 text-background-50" : "bg-foreground-800 text-background-300 hover:bg-foreground-700"
                }`}
              >
                按人气
              </button>
              <button
                onClick={() => setSort("likes")}
                className={`rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                  sort === "likes" ? "bg-accent-500 text-background-50" : "bg-foreground-800 text-background-300 hover:bg-foreground-700"
                }`}
              >
                按收藏
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* filter tabs */}
      <div className="sticky top-16 z-30 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-2 overflow-x-auto px-4 py-3 md:px-6">
          <button
            onClick={() => setFilter("all")}
            className={`shrink-0 rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${
              filter === "all" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            全部
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => setFilter(c.slug)}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm whitespace-nowrap transition-colors ${
                filter === c.slug ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* list */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <div className="space-y-3">
          {list.map((w, i) => (
            <Link
              key={w.id}
              to={`/webtoon/${w.slug}`}
              className="group flex items-center gap-4 rounded-2xl border border-background-200/70 bg-background-100 p-3 transition-all duration-300 hover:border-accent-200 hover:bg-background-50 md:gap-6 md:p-4"
            >
              <span
                className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl font-heading text-xl md:h-14 md:w-14 md:text-2xl ${
                  badgeStyles[i] ?? "bg-background-200 text-foreground-600"
                }`}
              >
                {i + 1}
              </span>
              <img
                src={w.cover}
                alt={`${w.title} 免费韩漫排行榜作品`}
                title={`${w.title} 人气推荐`}
                className="hidden h-20 w-14 shrink-0 rounded-lg object-cover object-top sm:block md:h-24 md:w-16"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-bold text-foreground-950 md:text-lg">{w.title}</h2>
                  <span className="rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-900">
                    {w.rating.toFixed(1)}
                  </span>
                  <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">{w.status}</span>
                </div>
                <p className="mt-1 line-clamp-2 text-sm text-foreground-600 md:line-clamp-1">{w.shortDesc}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                  <span>{w.author}</span>
                  <span className="text-background-300">·</span>
                  <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{w.views}</span>
                  <span className="text-background-300">·</span>
                  <span className="flex items-center gap-1"><i className="ri-heart-3-line"></i>{w.likes}</span>
                  <span className="text-background-300">·</span>
                  <span>{w.episode}话</span>
                  <div className="ml-1 flex flex-wrap gap-1.5">
                    {w.tags.slice(0, 3).map((t) => (
                      <span key={t} className="rounded-full bg-background-200 px-2 py-0.5 text-foreground-600">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
              <i className="ri-arrow-right-s-line text-xl text-foreground-400 transition-transform group-hover:translate-x-1 group-hover:text-accent-600"></i>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function parseViews(w: { views: string }): number {
  const v = w.views.replace("万", "0000");
  return parseInt(v.replace(/[^0-9]/g, ""), 10) || 0;
}